import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  Monitor,
  MapPin,
  Clock,
  Smartphone,
  Tablet,
  Laptop,
  AlertTriangle,
} from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import FetchErrorState from "../../Common/FetchErrorState";
import { getLoginActivity } from "../../../services/settingsService";

const LoginActivityView = ({ onBack }) => {
  const { apiCall } = useAuth();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLoginActivity();
  }, []);

  const fetchLoginActivity = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getLoginActivity(apiCall);

      if (result.success) {
        setDevices(result.devices);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError("Failed to load login activity");
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const getDeviceIcon = (deviceType) => {
    switch (deviceType) {
      case "mobile":
        return <Smartphone className="w-5 h-5 text-gray-600" />;
      case "tablet":
        return <Tablet className="w-5 h-5 text-gray-600" />;
      case "desktop":
        return <Laptop className="w-5 h-5 text-gray-600" />;
      default:
        return <Monitor className="w-5 h-5 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 px-4 sm:px-0">
        <div className="bg-white">
          <div className="flex items-center space-x-3 mb-4">
            <button onClick={onBack} className="p-2">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h2 className="text-xl font-semibold text-black">
                Login Activity
              </h2>
            </div>
          </div>
        </div>
        <div className="bg-white">
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="flex items-center justify-between p-4 border border-gray-400 rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-5 h-5 bg-gray-200 rounded"></div>
                  <div>
                    <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-48"></div>
                  </div>
                </div>
                <div className="h-6 bg-gray-200 rounded w-20"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 px-4 sm:px-0">
      {/* Header */}
      <div className="bg-white">
        <div className="flex items-center space-x-3 mb-4">
          <button onClick={onBack} className="p-2">
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h2 className="text-xl font-semibold text-black">Login Activity</h2>
          </div>
        </div>
      </div>

      {error ? (
        <FetchErrorState message={error} onRetry={fetchLoginActivity} />
      ) : (
        /* Active Sessions */
        <div className="bg-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-black">
              Active Sessions {devices.length > 0 && `(${devices.length})`}
            </h3>
          </div>

          {devices.length === 0 ? (
            <div className="text-center py-8 text-gray-600">
              <Monitor className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No active sessions found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {devices.map((device) => (
                <div
                  key={device.id}
                  className="flex items-center justify-between p-4 border border-gray-400 rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    {getDeviceIcon(device.deviceType)}
                    <div>
                      <p className="font-medium text-black text-sm">
                        {device.device}
                      </p>
                      <div className="flex items-center space-x-4 text-xs text-gray-600 mt-1">
                        <span className="flex items-center space-x-1">
                          <Clock className="w-3 h-3" />
                          <span>{formatTime(device.lastUsed)}</span>
                        </span>
                      </div>
                      <div className="flex items-center space-x-4 text-xs text-gray-600 mt-1">
                        <span>{device.browser}</span>
                        <span className="capitalize">{device.deviceType}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    {device.current && (
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                        Current
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LoginActivityView;
